/**
 * Template Item Editor
 * 
 * Form for editing inspection item properties:
 * - Name, description
 * - Required toggle
 * - Evidence mode
 * - Allowed evidence types
 * - AI policy
 */

import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Save } from 'lucide-react';
import type { TemplateItem } from '@/hooks/useInspectionTemplateEditor';
import { Json } from '@/integrations/supabase/types';

function arrayToEvidenceMap(val: unknown): Record<string, boolean> {
  if (Array.isArray(val)) {
    return Object.fromEntries(val.map((k: string) => [k, true]));
  }
  if (val && typeof val === 'object') return val as Record<string, boolean>;
  return {};
}

interface TemplateItemEditorProps {
  item: TemplateItem;
  onUpdate?: (updates: Partial<TemplateItem>) => Promise<void>;
  disabled?: boolean;
  isSaving?: boolean;
}

const EVIDENCE_MODES = [
  { value: 'NONE', label: 'None Required' },
  { value: 'OPTIONAL', label: 'Optional' },
  { value: 'REQUIRED', label: 'Required' },
];

const EVIDENCE_TYPES = [
  { key: 'photo', label: 'Photo' },
  { key: 'video', label: 'Video' },
  { key: 'note', label: 'Note' },
  { key: 'measurement', label: 'Measurement' },
  { key: 'ai', label: 'AI Analysis' },
];

const AI_POLICIES = [
  { value: 'DISABLED', label: 'No AI' },
  { value: 'SUGGEST_ONLY', label: 'AI Suggestions' },
  { value: 'AUTO_ALLOWED', label: 'Auto-Grade' },
];

export function TemplateItemEditor({
  item,
  onUpdate,
  disabled,
  isSaving,
}: TemplateItemEditorProps) {
  const [formData, setFormData] = useState({
    name: item.name,
    description: item.description || '',
    is_required: item.is_required,
    evidence_mode: item.evidence_mode,
    allowed_evidence: arrayToEvidenceMap(item.allowed_evidence),
    ai_policy: (item.ai_policy as string) || 'DISABLED',
  });

  const [hasChanges, setHasChanges] = useState(false);

  // Reset form when item changes
  useEffect(() => {
    setFormData({
      name: item.name,
      description: item.description || '',
      is_required: item.is_required,
      evidence_mode: item.evidence_mode,
      allowed_evidence: arrayToEvidenceMap(item.allowed_evidence),
      ai_policy: (item.ai_policy as string) || 'DISABLED',
    });
    setHasChanges(false);
  }, [item.id]);

  const updateField = <K extends keyof typeof formData>(field: K, value: typeof formData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const toggleEvidenceType = (key: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      allowed_evidence: { ...prev.allowed_evidence, [key]: checked },
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!onUpdate || disabled) return;

    await onUpdate({
      name: formData.name,
      description: formData.description || null,
      is_required: formData.is_required,
      evidence_mode: formData.evidence_mode,
      allowed_evidence: Object.entries(formData.allowed_evidence)
        .filter(([, v]) => v)
        .map(([k]) => k) as unknown as Json,
      ai_policy: formData.ai_policy,
    });

    setHasChanges(false);
  };

  return (
    <div className="space-y-4">
      {/* Item Key (read-only) */}
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Item Key</Label>
        <p className="text-sm font-mono bg-muted px-2 py-1 rounded">{item.item_key}</p>
      </div>

      {/* Name */}
      <div className="space-y-2">
        <Label htmlFor="itemName">Name</Label>
        <Input
          id="itemName"
          value={formData.name}
          onChange={(e) => updateField('name', e.target.value)}
          disabled={disabled}
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="itemDescription">Description</Label>
        <Textarea
          id="itemDescription"
          value={formData.description}
          onChange={(e) => updateField('description', e.target.value)}
          placeholder="Optional description or instructions..."
          rows={2}
          disabled={disabled}
        />
      </div>

      {/* Required Toggle */}
      <div className="flex items-center justify-between">
        <Label htmlFor="isRequired">Required</Label>
        <Switch
          id="isRequired"
          checked={formData.is_required}
          onCheckedChange={(checked) => updateField('is_required', checked)}
          disabled={disabled}
        />
      </div>

      {/* Evidence Mode */}
      <div className="space-y-2">
        <Label>Evidence Mode</Label>
        <Select
          value={formData.evidence_mode}
          onValueChange={(value) => updateField('evidence_mode', value)}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EVIDENCE_MODES.map((mode) => (
              <SelectItem key={mode.value} value={mode.value}>
                {mode.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Allowed Evidence Types */}
      {formData.evidence_mode !== 'NONE' && (
        <div className="space-y-2">
          <Label>Allowed Evidence</Label>
          <div className="grid grid-cols-2 gap-2">
            {EVIDENCE_TYPES.map((type) => (
              <div key={type.key} className="flex items-center space-x-2">
                <Checkbox
                  id={`evidence-${type.key}`}
                  checked={formData.allowed_evidence[type.key] ?? false}
                  onCheckedChange={(checked) => toggleEvidenceType(type.key, !!checked)}
                  disabled={disabled}
                />
                <Label
                  htmlFor={`evidence-${type.key}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {type.label}
                </Label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Policy */}
      <div className="space-y-2">
        <Label>AI Policy</Label>
        <Select
          value={formData.ai_policy}
          onValueChange={(value) => updateField('ai_policy', value)}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AI_POLICIES.map((policy) => (
              <SelectItem key={policy.value} value={policy.value}>
                {policy.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Save Button */}
      {!disabled && (
        <Button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className="w-full"
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Changes
        </Button>
      )}
    </div>
  );
}
